import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  alpha,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Divider,
  Tooltip,
  Popover,
  Checkbox,
} from '@mui/material';
import {
  Search as SearchIcon,
  Inventory as InventoryIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  ShoppingCart as ShoppingCartIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  Receipt as ReceiptIcon,
  Edit as EditIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Favorite as FavoriteFilledIcon,
  Build as BuildIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ProductWishlistDialog from '../../../components/ProductWishlistDialog';

function ProductGrid({ 
  products, 
  loading, 
  onSelectProduct, 
  showCustomProduct = true,
  isFullscreen = false,
  fullscreenContainer = null,
  onCustomProductClick,
  onRepairTicketClick, // ✅ New prop for repair ticket
  showRepairTicket = true, // ✅ Permission-based visibility for repair ticket
  cart = [], // ✅ Cart items to sync quantities
}) {
  const { t } = useTranslation();
  const [quantities, setQuantities] = useState({});
  const [editedPrices, setEditedPrices] = useState({});
  const [priceEditMode, setPriceEditMode] = useState({});
  const [updatePriceFlags, setUpdatePriceFlags] = useState({}); // ✅ Per-product "Update Price" toggle
  const [wishlistProduct, setWishlistProduct] = useState(null);
  const [localWishlistIds, setLocalWishlistIds] = useState(new Set());
  
  // ✅ Track which quantities were manually changed by user (to preserve during cart sync)
  const dirtyQuantities = useRef(new Set());

  // ✅ NEW: State for image magnifier
  const [imagePopover, setImagePopover] = useState({
    open: false,
    anchorEl: null,
    imageUrl: null,
    productName: null,
  });

  // ✅ Aggregate cart quantities by product ID (handles duplicate _rowKey entries)
  const cartQuantityMap = useMemo(() => {
    const map = new Map();
    cart.forEach(item => {
      const currentQty = map.get(item.Id) || 0;
      map.set(item.Id, currentQty + item.Quantity);
    });
    return map;
  }, [cart]);

  // ✅ Sync quantities from cart when cart or products change (category switch)
  useEffect(() => {
    setQuantities(prev => {
      const newQuantities = { ...prev };
      
      // Update quantities from cart, but preserve user-modified quantities
      cartQuantityMap.forEach((qty, productId) => {
        if (!dirtyQuantities.current.has(productId)) {
          newQuantities[productId] = qty;
        }
      });
      
      // Reset quantities for products no longer in cart (unless user-modified)
      Object.keys(newQuantities).forEach(productId => {
        const id = parseInt(productId);
        if (!cartQuantityMap.has(id) && !dirtyQuantities.current.has(id)) {
          delete newQuantities[productId];
        }
      });
      
      return newQuantities;
    });
  }, [cartQuantityMap, products]);

  const getQuantity = (productId) => {
    return quantities[productId] || 0;
  };

  const setQuantity = (productId, value) => {
    const newValue = Math.max(0, parseInt(value) || 0);
    dirtyQuantities.current.add(productId); // ✅ Mark as user-modified
    setQuantities(prev => ({ ...prev, [productId]: newValue }));
  };

  const incrementQuantity = (productId) => {
    const currentQty = quantities[productId] || 0;
    dirtyQuantities.current.add(productId); // ✅ Mark as user-modified
    setQuantities(prev => ({ ...prev, [productId]: currentQty + 1 }));
  };

  const decrementQuantity = (productId) => {
    const currentQty = quantities[productId] || 0;
    
    if (currentQty > 0) {
      dirtyQuantities.current.add(productId); // ✅ Mark as user-modified
      setQuantities(prev => ({ 
        ...prev, 
        [productId]: currentQty - 1
      }));
    }
  };

  const getCurrentPrice = (product) => {
    if (editedPrices[product.Id] !== undefined) {
      return editedPrices[product.Id];
    }
    return product.SalePrice || product.Price || 0;
  };

  const handlePriceEdit = (productId, value) => {
    const price = parseFloat(value) || 0;
    setEditedPrices(prev => ({ ...prev, [productId]: price }));
  };

  const togglePriceEditMode = (productId) => {
    setPriceEditMode(prev => ({ ...prev, [productId]: !prev[productId] }));
  };

  const handleAddToCart = (product) => {
    const quantity = getQuantity(product.Id);
    if (quantity > 0) {
      const currentPrice = getCurrentPrice(product);
      const originalPrice = product.SalePrice || product.Price || 0;
      const isPriceEdited = editedPrices[product.Id] !== undefined;
      
      
      const productWithPrice = {
        ...product,
        Price: currentPrice,
        SalePrice: currentPrice,
        OriginalPrice: originalPrice,
        IsPriceEdited: isPriceEdited,
        UpdatePrice: isPriceEdited ? (updatePriceFlags[product.Id] === true) : false,
      };
      
      onSelectProduct(productWithPrice, quantity);
      
      dirtyQuantities.current.delete(product.Id); // ✅ Clear dirty flag after adding to cart
      setQuantities(prev => ({ ...prev, [product.Id]: 0 }));
      setPriceEditMode(prev => ({ ...prev, [product.Id]: false }));
      setUpdatePriceFlags(prev => ({ ...prev, [product.Id]: false }));
      
      if (isPriceEdited) {
        toast.success(`${product.Name} added with custom price €${currentPrice.toFixed(2)}`);
      }
    }
  };

  const getProductImage = (product) => {
    if (product.ImageUrl) return product.ImageUrl;
    if (product.Images && product.Images.length > 0) return product.Images[0];
    return null;
  };

  const getStockColor = (stock) => {
    if (stock <= 0) return 'error';
    if (stock < 10) return 'warning';
    return 'success';
  };

  // ✅ NEW: Handle image hover for magnifier  
  const handleImageMouseEnter = (event, imageUrl, productName) => {
    if (imageUrl) {
      setImagePopover({
        open: true,
        anchorEl: event.currentTarget,
        imageUrl: imageUrl,
        productName: productName,
      });
    }
  };

  const handleImageMouseLeave = () => {
    setImagePopover({
      open: false,
      anchorEl: null,
      imageUrl: null,
      productName: null,
    });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  const hasNoProducts = !products || products.length === 0;

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      {/* Product Grid */}
      <Box
        sx={{
          height: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          pr: { xs: 0.5, sm: 1 },
          pb: 2,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: alpha('#ccc', 0.1),
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: alpha('#3498db', 0.3),
            borderRadius: '10px',
            '&:hover': {
              background: alpha('#3498db', 0.5),
            },
          },
        }}
      >
        {hasNoProducts ? (
          <Box>
            {/* Add New Product Widget (No Products) */}
            {showCustomProduct && (
              <Grid container spacing={{ xs: 1.5, sm: 2, md: 2 }}>
                <Grid item xs={12} sm={6} md={6} lg={4} xl={3}>
                  <Card
                    onClick={() => onCustomProductClick?.()}
                    sx={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.2s',
                      borderRadius: 2,
                      overflow: 'hidden',
                      boxShadow: 2,
                      cursor: 'pointer',
                      border: '2px dashed',
                      borderColor: '#667eea',
                      bgcolor: alpha('#667eea', 0.02),
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 6,
                        bgcolor: alpha('#667eea', 0.05),
                        borderColor: '#5568d3',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        height: { xs: 80, sm: 90, md: 100 },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: alpha('#667eea', 0.08),
                      }}
                    >
                      <AddCircleOutlineIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: '#667eea' }} />
                    </Box>

                    <CardContent sx={{ p: { xs: 1, sm: 1.5 }, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexGrow: 1, minHeight: { xs: 50, sm: 60, md: 70 } }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
                          color: '#667eea',
                          textAlign: 'center',
                          lineHeight: 1.3,
                        }}
                      >
                        {t('add_new_product') || 'Add New Product'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                {/* Repair Ticket Widget (No Products) */}
                {showRepairTicket && (
                  <Grid item xs={12} sm={6} md={6} lg={4} xl={3}>
                    <Card
                      onClick={() => onRepairTicketClick?.()}
                      sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.2s',
                        borderRadius: 2,
                        overflow: 'hidden',
                        boxShadow: 2,
                        cursor: 'pointer',
                        border: '2px dashed',
                        borderColor: 'secondary.main',
                        bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.02),
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 6,
                          bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.05),
                          borderColor: 'secondary.dark',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          height: { xs: 80, sm: 90, md: 100 },
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.08),
                        }}
                      >
                        <BuildIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: 'secondary.main' }} />
                      </Box>

                      <CardContent sx={{ p: { xs: 1, sm: 1.5 }, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexGrow: 1, minHeight: { xs: 50, sm: 60, md: 70 } }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
                            color: 'secondary.main',
                            textAlign: 'center',
                            lineHeight: 1.3,
                          }}
                        >
                          {t('create_repair_ticket') || 'Create Repair Ticket'}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            )}

            {/* No Products Message */}
            <Box
              sx={{
                textAlign: 'center',
                py: { xs: 4, sm: 8 },
                color: 'text.secondary',
              }}
            >
              <InventoryIcon sx={{ fontSize: { xs: 60, sm: 80 }, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                {t('no_products_found') || 'No products found'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                {t('add_custom_product_hint') || 'You can still add custom products'}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Grid container spacing={{ xs: 1.5, sm: 2, md: 2 }}>
            {/* Add New Product Widget */}
            {showCustomProduct && (
              <Grid item xs={12} sm={6} md={6} lg={4} xl={3}>
                <Card
                  onClick={() => onCustomProductClick?.()}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.2s',
                    borderRadius: 2,
                    overflow: 'hidden',
                    boxShadow: 2,
                    cursor: 'pointer',
                    border: '2px dashed',
                    borderColor: '#667eea',
                    bgcolor: alpha('#667eea', 0.02),
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 6,
                      bgcolor: alpha('#667eea', 0.05),
                      borderColor: '#5568d3',
                    },
                  }}
                >
                  <Box
                    sx={{
                      height: { xs: 80, sm: 90, md: 100 },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha('#667eea', 0.08),
                    }}
                  >
                    <AddCircleOutlineIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: '#667eea' }} />
                  </Box>

                  <CardContent sx={{ p: { xs: 1, sm: 1.5 }, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexGrow: 1, minHeight: { xs: 50, sm: 60, md: 70 } }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
                        color: '#667eea',
                        textAlign: 'center',
                        lineHeight: 1.3,
                      }}
                    >
                      {t('add_new_product') || 'Add New Product'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
            
            {/* Repair Ticket Widget */}
            {showRepairTicket && (
              <Grid item xs={12} sm={6} md={6} lg={4} xl={3}>
                <Card
                  onClick={() => onRepairTicketClick?.()}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.2s',
                    borderRadius: 2,
                    overflow: 'hidden',
                    boxShadow: 2,
                    cursor: 'pointer',
                    border: '2px dashed',
                    borderColor: 'secondary.main',
                    bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.02),
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 6,
                      bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.05),
                      borderColor: 'secondary.dark',
                    },
                  }}
                >
                  <Box
                    sx={{
                      height: { xs: 80, sm: 90, md: 100 },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.08),
                    }}
                  >
                    <BuildIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: 'secondary.main' }} />
                  </Box>

                  <CardContent sx={{ p: { xs: 1, sm: 1.5 }, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexGrow: 1, minHeight: { xs: 50, sm: 60, md: 70 } }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
                        color: 'secondary.main',
                        textAlign: 'center',
                        lineHeight: 1.3,
                      }}
                    >
                      {t('create_repair_ticket') || 'Create Repair Ticket'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Regular Products */}
            {products.map((product) => {
              const productImage = getProductImage(product);
              const currentQuantity = getQuantity(product.Id);
              const currentPrice = getCurrentPrice(product);
              const isPriceEditing = priceEditMode[product.Id];
              const isPriceEdited = editedPrices[product.Id] !== undefined;
              const maxStock = product.Stock || product.StockQuantity || 0;
              const canDecrement = currentQuantity > 0;
              const canAddToCart = currentQuantity > 0;
              const isInCart = cartQuantityMap.has(product.Id); // ✅ Check if product is in cart
              const cartQuantity = cartQuantityMap.get(product.Id) || 0; // ✅ Get cart quantity
              
              return (
                <Grid item xs={12} sm={6} md={6} lg={4} xl={3} key={product.Id}>
                  <Card
                    sx={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.2s',
                      borderRadius: 2,
                      overflow: 'hidden',
                      boxShadow: isInCart ? 4 : 2,
                      border: isInCart 
                        ? '2px solid #4caf50' 
                        : isPriceEdited ? '2px solid #ff9800' : 'none',
                      position: 'relative',
                      bgcolor: isInCart ? alpha('#4caf50', 0.02) : 'background.paper',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: isInCart ? 8 : 6,
                      },
                    }}
                  >
                    {/* ✅ FIXED: Product Image with proper object-fit and hover magnifier */}
                    {productImage ? (
                      <Box
                        sx={{
                          height: { xs: 100, sm: 120, md: 140, lg: 160, xl: 180 },
                          width: '100%',
                          position: 'relative',
                          overflow: 'hidden',
                          bgcolor: '#f5f5f5',
                          cursor: 'zoom-in',
                        }}
                        onMouseEnter={(e) => handleImageMouseEnter(e, productImage, product.Name)}
                        onMouseLeave={handleImageMouseLeave}
                      >
                        {/* ✅ Cart Quantity Badge */}
                        {isInCart && (
                          <Chip
                            label={`In Cart: ${cartQuantity}`}
                            size="small"
                            color="success"
                            sx={{
                              position: 'absolute',
                              top: 4,
                              left: 4,
                              zIndex: 2,
                              fontWeight: 700,
                              fontSize: { xs: '0.55rem', sm: '0.6rem', md: '0.65rem' },
                              height: { xs: 16, sm: 18, md: 20 },
                              bgcolor: '#4caf50',
                              color: 'white',
                              boxShadow: '0 2px 8px rgba(76, 175, 80, 0.4)',
                              '& .MuiChip-label': {
                                px: { xs: 0.4, sm: 0.6, md: 1 },
                              }
                            }}
                          />
                        )}
                        <CardMedia
                          component="img"
                          image={productImage}
                          alt={product.Name}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain', // ✅ Changed from 'cover' to 'contain' to show full image
                            objectPosition: 'center',
                            transition: 'transform 0.2s',
                            '&:hover': {
                              transform: 'scale(1.05)',
                            },
                          }}
                        />
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          height: { xs: 100, sm: 120, md: 140, lg: 160, xl: 180 },
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: alpha('#667eea', 0.08),
                        }}
                      >
                        <ShoppingCartIcon sx={{ fontSize: { xs: 30, sm: 40 }, color: alpha('#667eea', 0.4) }} />
                      </Box>
                    )}

<CardContent sx={{ p: { xs: 0.75, sm: 1, md: 1.5 }, display: 'flex', flexDirection: 'column', gap: { xs: 0.4, sm: 0.6, md: 0.75 } }}>
                      {/* Product Name */}
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          lineHeight: 1.3,
                          minHeight: { xs: 28, sm: 32, md: 36 },
                        }}
                        title={product.Name}
                      >
                        {product.Name}
                      </Typography>

                      {/* Price and Stock Section */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 0.4, sm: 0.6, md: 0.75 } }}>
                        {/* Stock Chip */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Chip
                            label={`Stock: ${maxStock}`}
                            size="small"
                            color={getStockColor(maxStock)}
                            sx={{ 
                              fontWeight: 600, 
                              fontSize: { xs: '0.55rem', sm: '0.6rem', md: '0.65rem' },
                              height: { xs: 16, sm: 18, md: 20 },
                              minWidth: { xs: 45, sm: 50, md: 60 },
                              '& .MuiChip-label': {
                                px: { xs: 0.4, sm: 0.5, md: 1 },
                              }
                            }}
                          />
                        </Box>

                        {/* Price Section */}
                        {isPriceEditing ? (
                          <TextField
                            type="number"
                            value={currentPrice}
                            onChange={(e) => handlePriceEdit(product.Id, e.target.value)}
                            onBlur={() => togglePriceEditMode(product.Id)}
                            autoFocus
                            fullWidth
                            size="small"
                            InputProps={{
                              startAdornment: <InputAdornment position="start">€</InputAdornment>,
                              style: { 
                                fontSize: { xs: '0.8rem', sm: '0.85rem', md: '1rem' },
                                fontWeight: 700,
                                color: '#ff9800',
                              }
                            }}
                            inputProps={{ 
                              min: 0, 
                              step: 0.01,
                              style: { padding: '3px 4px' }
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                  borderColor: '#ff9800',
                                  borderWidth: 2,
                                },
                              },
                            }}
                          />
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
                            <Typography
                              variant="h6"
                              sx={{
                                color: isPriceEdited ? '#ff9800' : 'primary.main',
                                fontWeight: 700,
                                fontSize: { xs: '0.8rem', sm: '0.85rem', md: '1rem' },
                              }}
                            >
                              €{currentPrice.toFixed(2)}
                            </Typography>
                            <Tooltip title={t('edit_price') || 'Edit Price'}>
                              <IconButton
                                size="small"
                                onClick={() => togglePriceEditMode(product.Id)}
                                sx={{
                                  width: { xs: 18, sm: 22, md: 24 },
                                  height: { xs: 18, sm: 22, md: 24 },
                                  color: isPriceEdited ? '#ff9800' : '#999',
                                  '&:hover': {
                                    color: '#ff9800',
                                    bgcolor: alpha('#ff9800', 0.1),
                                  },
                                }}
                              >
                                <EditIcon sx={{ fontSize: { xs: 13, sm: 15, md: 16 } }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}

                        {/* Original price + Update Price checkbox if edited */}
                        {isPriceEdited && !isPriceEditing && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                                textDecoration: 'line-through',
                                fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' },
                              }}
                            >
                              Original: €{(product.SalePrice || product.Price || 0).toFixed(2)}
                            </Typography>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={updatePriceFlags[product.Id] === true}
                                  onChange={() =>
                                    setUpdatePriceFlags(prev => ({
                                      ...prev,
                                      [product.Id]: !prev[product.Id],
                                    }))
                                  }
                                  sx={{
                                    p: 0,
                                    mr: 0.25,
                                    color: '#ff9800',
                                    '&.Mui-checked': { color: '#ff9800' },
                                  }}
                                />
                              }
                              label={
                                <Typography variant="caption" sx={{ fontSize: { xs: '0.58rem', sm: '0.6rem', md: '0.62rem' }, color: '#e65100', fontWeight: 600, lineHeight: 1.2 }}>
                                  Update price in DB
                                </Typography>
                              }
                              sx={{ m: 0, alignItems: 'center' }}
                            />
                          </Box>
                        )}
                      </Box>

                      {/* Quantity Controls */}
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: { xs: 0.4, sm: 0.6, md: 0.75 },
                          bgcolor: alpha('#667eea', 0.05),
                          borderRadius: 1.5,
                          p: { xs: 0.3, sm: 0.4, md: 0.5 },
                          mt: { xs: 0.2, sm: 0.3, md: 0.5 },
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => decrementQuantity(product.Id)}
                          disabled={!canDecrement}
                          sx={{
                            bgcolor: canDecrement ? '#ff5252' : '#ffcdd2',
                            color: 'white',
                            width: { xs: 22, sm: 26, md: 28 },
                            height: { xs: 22, sm: 26, md: 28 },
                            minWidth: { xs: 22, sm: 26, md: 28 },
                            '&:hover': { 
                              bgcolor: canDecrement ? '#ff1744' : '#ffcdd2',
                            },
                            '&.Mui-disabled': { 
                              bgcolor: '#ffcdd2',
                              color: '#fff',
                              opacity: 0.6,
                            },
                            p: 0,
                          }}
                        >
                          <RemoveIcon sx={{ fontSize: { xs: 11, sm: 14, md: 16 } }} />
                        </IconButton>

                        <TextField
                          type="number"
                          value={currentQuantity}
                          onChange={(e) => setQuantity(product.Id, e.target.value)}
                          inputProps={{ 
                            min: 0,
                            style: { 
                              textAlign: 'center',
                              padding: '2px 1px',
                              fontWeight: 700,
                              fontSize: window.innerWidth < 600 ? '0.7rem' : window.innerWidth < 900 ? '0.75rem' : '0.85rem',
                            } 
                          }}
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            '& .MuiOutlinedInput-root': {
                              height: { xs: 22, sm: 26, md: 28 },
                              '& fieldset': { border: 'none' },
                            },
                            '& input': {
                              color: currentQuantity > 0 ? '#667eea' : '#999',
                            }
                          }}
                        />

                        <IconButton
                          size="small"
                          onClick={() => incrementQuantity(product.Id)}
                          sx={{
                            bgcolor: '#4caf50',
                            color: 'white',
                            width: { xs: 22, sm: 26, md: 28 },
                            height: { xs: 22, sm: 26, md: 28 },
                            minWidth: { xs: 22, sm: 26, md: 28 },
                            '&:hover': { 
                              bgcolor: '#43a047',
                            },
                            p: 0,
                          }}
                        >
                          <AddIcon sx={{ fontSize: { xs: 11, sm: 14, md: 16 } }} />
                        </IconButton>
                      </Box>

                      {/* Wishlist Button */}
                      {(() => {
                        const isWishlisted = product.IsWishlistProduct || localWishlistIds.has(product.Id);
                        return (
                          <Tooltip title={isWishlisted ? t('in_wishlist') || 'In Wishlist' : t('add_to_wishlist') || 'Add to Wishlist'}>
                            <span>
                              <IconButton
                                size="small"
                                disabled={isWishlisted}
                                onClick={() => setWishlistProduct({
                                  Id: product.Id,
                                  Name: product.Name,
                                  SKU: product.SKU,
                                  SalePrice: product.SalePrice || product.Price || 0,
                                  StockQuantity: product.Stock || product.StockQuantity || 0,
                                  SupplierId: product.SupplierId || null,
                                })}
                                sx={{
                                  position: 'absolute',
                                  top: 4,
                                  right: 4,
                                  zIndex: 2,
                                  bgcolor: 'rgba(255,255,255,0.85)',
                                  width: { xs: 20, sm: 24, md: 26 },
                                  height: { xs: 20, sm: 24, md: 26 },
                                  color: isWishlisted ? 'error.main' : 'action.active',
                                  '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' },
                                  '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.85)', color: 'error.main' },
                                }}
                              >
                                {isWishlisted
                                  ? <FavoriteFilledIcon sx={{ fontSize: { xs: 12, sm: 14, md: 15 } }} />
                                  : <FavoriteBorderIcon sx={{ fontSize: { xs: 12, sm: 14, md: 15 } }} />
                                }
                              </IconButton>
                            </span>
                          </Tooltip>
                        );
                      })()}

                      {/* Add to Cart Button */}
                      <Button
                        fullWidth
                        variant="contained"
                        size="small"
                        startIcon={<ShoppingCartIcon sx={{ fontSize: { xs: 11, sm: 14, md: 16 } }} />}
                        onClick={() => handleAddToCart(product)}
                        disabled={!canAddToCart}
                        sx={{
                          py: { xs: 0.4, sm: 0.6, md: 0.75 },
                          px: { xs: 0.5, sm: 1 },
                          fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.75rem' },
                          fontWeight: 600,
                          borderRadius: 1.5,
                          textTransform: 'none',
                          minHeight: { xs: 26, sm: 28, md: 32 },
                          background: canAddToCart
                            ? isInCart
                              ? 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)'
                              : isPriceEdited 
                                ? 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)'
                                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                            : '#e0e0e0',
                          color: canAddToCart ? 'white' : '#9e9e9e',
                          boxShadow: canAddToCart 
                            ? isInCart
                              ? '0 2px 6px rgba(76, 175, 80, 0.4)'
                              : '0 2px 6px rgba(102, 126, 234, 0.3)'
                            : 'none',
                          '&:hover': {
                            background: canAddToCart
                              ? isInCart
                                ? 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)'
                                : isPriceEdited
                                  ? 'linear-gradient(135deg, #f57c00 0%, #e65100 100%)'
                                  : 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)' 
                              : '#e0e0e0',
                            boxShadow: canAddToCart 
                              ? isInCart
                                ? '0 3px 10px rgba(76, 175, 80, 0.5)'
                                : '0 3px 10px rgba(102, 126, 234, 0.4)'
                              : 'none',
                            transform: canAddToCart ? 'translateY(-1px)' : 'none',
                          },
                          '&.Mui-disabled': {
                            background: '#e0e0e0',
                            color: '#9e9e9e',
                          },
                        }}
                      >
                        {isInCart ? (t('update_cart') || 'Update Cart') : (t('add_to_cart') || 'Add')}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>

      {/* ✅ NEW: Image Magnifier Popover */}
      <Popover
        open={imagePopover.open}
        anchorEl={imagePopover.anchorEl}
        onClose={handleImageMouseLeave}
        disableRestoreFocus
        disableScrollLock
        disableEnforceFocus
        disableAutoFocus
        sx={{
          pointerEvents: 'none', // ✅ Allows mouse to pass through
        }}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            pointerEvents: 'none',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            borderRadius: 2,
            overflow: 'hidden',
            border: '2px solid #667eea',
            bgcolor: 'white',
          },
        }}
      >
        <Box
          sx={{
            width: { xs: 250, sm: 300, md: 350 },
            height: { xs: 250, sm: 300, md: 350 },
            position: 'relative',
            bgcolor: '#f5f5f5',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Product Name Header */}
          <Box
            sx={{
              p: 1.5,
              bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                fontSize: '0.85rem',
                textAlign: 'center',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {imagePopover.productName}
            </Typography>
          </Box>

          {/* Magnified Image */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
            }}
          >
            <img
              src={imagePopover.imageUrl}
              alt={imagePopover.productName}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                borderRadius: '8px',
              }}
            />
          </Box>
        </Box>
      </Popover>

      {/* Add to Wishlist Dialog */}
      <ProductWishlistDialog
        open={!!wishlistProduct}
        onClose={() => setWishlistProduct(null)}
        product={wishlistProduct}
        onSuccess={() => {
          const addedId = wishlistProduct?.Id;
          setWishlistProduct(null);
          if (addedId) {
            setLocalWishlistIds(prev => new Set([...prev, addedId]));
          }
          toast.success(t('added_to_wishlist') || 'Product added to wishlist!');
        }}
      />
    </Box>
  );
}

export default ProductGrid;